import { useState } from "react";

import { HStack, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import useValueToLabelCache from "@/hooks/useValueToLabelCache";
import { defaultNS, useTranslation } from "@/lib/i18n";

import Combobox from "../index";
import type { ComboboxOption, ComboboxOptionRenderer } from "../types";

import { useSoftwaresForSelect } from "./-query";

export type Software = Readonly<{
  id?: string;
  appIntegrationId?: string;
  name: string;
  type: string;
}>;

interface SoftwareSelectorComboboxProps {
  onSelect: (software: Software) => void;
  disabledSoftwareIds?: Set<string>;
  onClose: () => void;
}

const renderSoftwareOption: ComboboxOptionRenderer<ComboboxOption> = ({
  option,
}) => (
  <HStack className="items-center gap-3">
    <VStack>
      <Text variant="labelSmall">{option.label}</Text>
    </VStack>
  </HStack>
);

export default function SoftwareSelectorCombobox({
  onSelect,
  disabledSoftwareIds = new Set(),
  onClose,
}: SoftwareSelectorComboboxProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "component.Combobox.softwareSelectorCombobox",
  });

  const [searchText, setSearchText] = useState("");
  const [value, setValue] = useState<string | null>(null);
  const valueToLabelCache = useValueToLabelCache();

  const {
    data: options = [],
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useSoftwaresForSelect(searchText, disabledSoftwareIds);

  const handleValueChange = (newValue: string | null) => {
    if (!newValue) return;

    setValue(newValue);
    const selectedOption = options.find(
      (option: { value: string }) => option.value === newValue,
    );
    if (!selectedOption) return;

    // valueには{id, appIntegrationId}がJSONStringifyされている
    const parsedValue = JSON.parse(selectedOption.value);
    onSelect({
      id: parsedValue.id,
      appIntegrationId: parsedValue.appIntegrationId,
      name: selectedOption.label,
      type: "",
    });
    onClose();
  };

  const handleScrollToEnd = () => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  };

  return (
    <Combobox
      type="single"
      value={value}
      options={options}
      isFetching={isLoading || isFetchingNextPage}
      onValueChange={handleValueChange}
      searchPlaceholder={t("searchSoftwarePlaceholder")}
      onSearchChange={useDebouncedCallback((text) => setSearchText(text), [])}
      valueToLabelCache={valueToLabelCache}
      renderOption={renderSoftwareOption}
      onScrollToEnd={handleScrollToEnd}
    />
  );
}
